// ---------------------------------------------------------------------------
// The campaign wizard's Apollo calls from the browser, over REST (R11d)
//
// The lead step and the sequence step asked BaseQL over the socket
// (`searchApolloLeads`, `getApolloLeadDetail`, `enrichApolloLeadsForExport`).
// They call these routes now (entrypoints/sdr/src/server/routes/apollo.ts),
// all behind the login, all POST with a JSON body — they spend credits, and a
// search's filters or a list of person ids must never stand in a URL:
//
//   POST /api/apollo/search          {input}            → {ok, result}
//   POST /api/apollo/lead-detail     {apolloPersonId}   → {ok, lead}
//   POST /api/apollo/export-enrich   {apolloPersonIds}  → {ok, leads}
//
// The server holds the limits (a search reaches at most the first 1,000 leads,
// an export enriches at most 1,000) and refuses in words the page can show. A
// refusal throws with the server's own words — Apollo's ("Apollo lead search
// failed: …"), the hold's, the limit's. With no words of the server's (a
// dropped connection, a page that is not JSON) it throws with the words the
// page falls back on.
//
// The shapes below are the wire format: plain data, an optional field that has
// no value is absent. No BaseQL entity is involved.
// ---------------------------------------------------------------------------

/** The search the wizard sends. Revenue in US dollars (the form's millions × 1,000,000). */
export interface ApolloLeadSearchInput {
  icpDescription?: string;
  companyLocation?: string[];
  companyIndustry?: string[];
  industryKeywords?: string;
  employeeCount?: string[];
  revenueMin?: number;
  revenueMax?: number;
  jobTitle?: string;
  department?: string[];
  personLocation?: string[];
  organizationNames?: string[];
  page?: number;
  perPage?: number;
  /** Ask the model for a fresh interpretation ("Re-interpret ICP"). */
  forceRemap?: boolean;
  /** People never to return: the project's earlier campaigns and the removed ones. */
  excludeApolloPersonIds?: string[];
  /**
   * Where in Apollo's results this page begins: the `nextOffset` the page
   * before returned (R17a2). Apollo's page n is not where the list stands once
   * anyone is left out; without it, the leads in between are never shown.
   */
  offset?: number;
}

/** One lead of the preview. The search never reveals an address: `email` is ''. */
export interface ApolloLeadPreview {
  apolloPersonId: string;
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  jobTitle: string;
  linkedinUrl?: string;
  industry?: string;
  employeeCount?: number;
  lastRefreshedAt?: string;
  hasEmail?: boolean;
  hasDirectPhone?: string;
}

/** The filters the search ran with: the form's fields and what the model read from the ICP. */
export interface ApolloAppliedFilters {
  personTitles: string[];
  personSeniorities: string[];
  personLocations: string[];
  organizationLocations: string[];
  organizationNames?: string[];
  organizationIndustryKeywords: string[];
  employeeRanges: string[];
  departments: string[];
  revenueMinUsd?: number;
  revenueMaxUsd?: number;
  qKeywords?: string;
  mappingFailed?: boolean;
}

export interface ApolloLeadSearchResult {
  totalEntries: number;
  leads: ApolloLeadPreview[];
  appliedFilters?: ApolloAppliedFilters;
  /** Where the next page of this list begins in Apollo's results: send it back as `offset`. */
  nextOffset?: number;
}

/** One person, enriched: what the lead-detail dialog shows. */
export interface ApolloLeadDetail {
  firstName: string;
  lastName: string;
  email: string;
  emailStatus?: string;
  phone?: string;
  linkedinUrl?: string;
  jobTitle?: string;
  company?: string;
  industry?: string;
  city?: string;
  state?: string;
  country?: string;
}

/** One CSV row after enrichment; only `apolloPersonId` when Apollo found no match or could not be asked. */
export interface ApolloEnrichedLead {
  apolloPersonId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  linkedinUrl?: string;
  jobTitle?: string;
  company?: string;
  industry?: string;
}

/**
 * The export's enrichment: a row per lead, and — when Apollo could not be
 * asked about some of them (an error, its rate limit) — how many and what to
 * do, in the server's words. Those rows keep the preview's data (R17a2).
 */
export interface ApolloExportEnrichment {
  leads: ApolloEnrichedLead[];
  notEnriched?: { people: number; error: string };
}

/**
 * The form's revenue in millions as the whole US dollars the search takes
 * (the server refuses a fraction, as the socket's GraphQL Int did). Rounded:
 * 4.1 × 1,000,000 is 4,099,999.9999999995 in JavaScript. An empty field is
 * left out.
 */
export const revenueUsd = (millions: number | null | undefined): number | undefined =>
  millions != null ? Math.round(millions * 1_000_000) : undefined;

/**
 * The first leads a search may reach. The server refuses a page whose window
 * passes them (routes/apollo.ts MAX_SEARCH_LEADS), in these words.
 */
export const MAX_SEARCH_LEADS = 1000;
export const TOO_MANY_LEADS = 'A search reaches at most the first 1,000 leads.';

/**
 * Whether page `page` of `perPage` stays within the first MAX_SEARCH_LEADS
 * leads: the server's rule (pages count from 1, so this also holds perPage to
 * 1,000). Left out, the page is 1 and the count 5, the search's own defaults,
 * as on the server.
 */
export const withinSearchReach = (page = 1, perPage = 5): boolean => page * perPage <= MAX_SEARCH_LEADS;

/**
 * The page "Load N more" asks next, or null when that page would pass the
 * first 1,000 leads: the preview has gone as far as a search may, and ends as
 * it does when Apollo has no more (page 40 of 25 is the last).
 */
export const nextPreviewPage = (pagesLoaded: number, perPage: number): number | null =>
  withinSearchReach(pagesLoaded + 1, perPage) ? pagesLoaded + 1 : null;

export const APOLLO_SEARCH_URL = '/api/apollo/search';
export const APOLLO_LEAD_DETAIL_URL = '/api/apollo/lead-detail';
export const APOLLO_EXPORT_ENRICH_URL = '/api/apollo/export-enrich';

/** The words the page falls back on when the server gives none. */
export const APOLLO_FAILED = {
  search: 'Apollo lead search failed',
  detail: 'Failed to load lead details',
  export: 'Lead enrichment failed — exporting with preview data only',
} as const;

const isObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

/** The body of a JSON answer, or null when there is none. */
async function bodyOf(res: Response): Promise<Record<string, unknown> | null> {
  try {
    const body: unknown = await res.json();
    return isObject(body) ? body : null;
  } catch {
    return null;
  }
}

/** One POST with a JSON body: the answer's body when the server said ok, else an Error with its words or `fallback`. */
async function post(url: string, payload: unknown, fallback: string): Promise<Record<string, unknown>> {
  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch {
    throw new Error(fallback);
  }
  const body = await bodyOf(res);
  if (res.ok && body?.ok === true) return body;
  const reason = typeof body?.error === 'string' && body.error ? body.error : fallback;
  throw Object.assign(new Error(reason), { status: res.status });
}

/**
 * Sample leads for the search, with the filters it ran with. A page past the
 * first 1,000 leads is never sent: the server would refuse it, so this throws
 * its words without asking. A call site ends its list before that
 * (nextPreviewPage).
 */
export async function searchApolloLeads(input: ApolloLeadSearchInput): Promise<ApolloLeadSearchResult> {
  if (!withinSearchReach(input.page, input.perPage)) throw new Error(TOO_MANY_LEADS);
  const { result } = await post(APOLLO_SEARCH_URL, { input }, APOLLO_FAILED.search);
  if (!isObject(result) || !Array.isArray(result.leads)) throw new Error(APOLLO_FAILED.search);
  return result as unknown as ApolloLeadSearchResult;
}

/** One person's details — about one Apollo credit. */
export async function getApolloLeadDetail(apolloPersonId: string): Promise<ApolloLeadDetail> {
  const { lead } = await post(APOLLO_LEAD_DETAIL_URL, { apolloPersonId }, APOLLO_FAILED.detail);
  if (!isObject(lead)) throw new Error(APOLLO_FAILED.detail);
  return lead as unknown as ApolloLeadDetail;
}

/**
 * Real names, addresses and LinkedIn URLs for the CSV export — about one
 * Apollo credit per matched lead. Throws when Apollo could not be asked about
 * any of them; names the ones it could not be asked about otherwise.
 */
export async function enrichApolloLeadsForExport(apolloPersonIds: string[]): Promise<ApolloExportEnrichment> {
  const { leads, notEnriched } = await post(APOLLO_EXPORT_ENRICH_URL, { apolloPersonIds }, APOLLO_FAILED.export);
  if (!Array.isArray(leads)) throw new Error(APOLLO_FAILED.export);
  const enrichment: ApolloExportEnrichment = { leads: leads as ApolloEnrichedLead[] };
  if (isObject(notEnriched) && typeof notEnriched.error === 'string' && notEnriched.error)
    enrichment.notEnriched = {
      people: typeof notEnriched.people === 'number' ? notEnriched.people : 0,
      error: notEnriched.error,
    };
  return enrichment;
}
